import React, { useState, useEffect, ReactElement, useContext } from 'react';
import ScoresPanel from 'components/commandpanel/ExperimentPanel/ScoresPanel';
import Experiment from 'components/main/Experiment';
import {
  DisplayModes,
  ExperimentRoutes,
  GetDisplayMode,
  GetScoreDefn,
  SetDisplayMode,
  SetExpRoute,
  customTabIconProps,
} from 'utils/Global';
import withPerf from 'components/customComponents/withPerf';
import { useRMSelector } from 'redux/hooks';
import { selectScoreGroups } from 'redux/selectors/StateSelectors';
import { BaseSidePanel } from 'components/sidePanel';
import { ITabItem } from 'components/tab';
import { IStyle } from 'Styles';
import RMSvgIcon from '../CommonComponents/RMSvgIcon';
import { EProductEditions } from 'enums/EProductEditions';
import useRMLite from 'CustomHooks/useRMLite';
import usePlateGenerated from './hooks';
import {
  ExperimentSideBarWidth,
  DisplayMode,
  isScoringFeatureEnabled,
} from 'components/Experiment/constants';
import { ExpDashboardContext } from 'components/main/ExperimentDashboardContext';
import { useParams } from 'react-router';
import useProductEdition from 'CustomHooks/useProductEdition';

let expMode = DisplayModes.DESIGN;
interface CustomTabProps {
  Icon: ReactElement<SVGElement>;
  title?: string;
  titleTip?: string;
  active: any;
  id: any;
}
export const CustomTab = (props: CustomTabProps) => {
  const { Icon, title, titleTip, active, id } = props;
  return (
    <div
      id="customcomponent-tab-wrapper"
      className={active === id ? 'active-custom-tab' : 'custom-tab-common cursor-pointer'}
      title={titleTip}
    >
      {Icon}
      {title && <div id={`customcomponent-tab-${title}`}>{title}</div>}
    </div>
  );
};

export default withPerf((props) => {
  const [refresh, setRefresh] = useState(false);
  const { isImagedExperiment } = props;
  const { wellNum } = useParams();
  const scoreGroups = useRMSelector(selectScoreGroups);
  SetExpRoute(ExperimentRoutes.CANVAS);
  const productEdition: EProductEditions = useProductEdition();
  const rmLite = useRMLite();
  const isPlateGenerated = usePlateGenerated();
  const renderDesignTab = !rmLite;
  const renderImageTab = isImagedExperiment || isPlateGenerated;
  const renderScoreView = isPlateGenerated && isScoringFeatureEnabled(productEdition);
  const rmLiteNewExp = rmLite && !isPlateGenerated;
  const { canvasWidth, ...rest } = props;
  const { selectedCanvasRoute, setSelectedCanvasRoute, registerDesignTabSwitcher } =
    useContext(ExpDashboardContext);

  function changeTabMode(tbMode) {
    expMode = tbMode;
    if (tbMode === DisplayModes.IMAGE) {
      SetDisplayMode(DisplayModes.IMAGE);
      props.changeHeader('showImage');
      setSelectedCanvasRoute(DisplayMode.IMAGE);
    } else if (tbMode === DisplayModes.DESIGN) {
      SetDisplayMode(DisplayModes.DESIGN);
      props.changeHeader('showDesign');
      setSelectedCanvasRoute(DisplayMode.DESIGN);
    } else if (tbMode === DisplayModes.WELLINGREDIENT) {
      SetDisplayMode(DisplayModes.WELLINGREDIENT);
      props.changeHeader('showWellIngredients');
      setSelectedCanvasRoute(DisplayMode.WELLINGREDIENT);
    } else if (tbMode === DisplayModes.SCORE) {
      SetDisplayMode(DisplayModes.SCORE);
      props.changeHeader('showScore');
      setSelectedCanvasRoute(DisplayMode.SCORE);
    }
  }

  useEffect(() => {
    registerDesignTabSwitcher?.(() => changeTabMode(DisplayModes.DESIGN));
    return () => registerDesignTabSwitcher?.(null);
  }, [registerDesignTabSwitcher]);

  useEffect(() => {
    if (selectedCanvasRoute) {
      changeTabMode(selectedCanvasRoute);
      return;
    }
    if (
      (isImagedExperiment && productEdition !== EProductEditions.ExperimentDesigner) ||
      rmLiteNewExp
    ) {
      expMode = DisplayModes.IMAGE;
    } else if (expMode === DisplayModes.DESIGN) {
      SetDisplayMode(DisplayModes.DESIGN);
    } else {
      expMode = GetDisplayMode();
    }
    changeTabMode(expMode);
  }, [wellNum, selectedCanvasRoute]);

  if (refresh) {
    setRefresh(false);
  }

  function scoreCommandPanelComponent() {
    if (expMode !== DisplayModes.IMAGESCORE) {
      return null;
    }
    const scoreDefns = GetScoreDefn() ?? [];
    const hasManualSelected =
      scoreGroups.length === 0 ||
      scoreGroups.some((scoreId) => {
        const scoreDefn = scoreDefns.find((defn) => defn.wellDropScoreId === scoreId);
        return scoreDefn && !scoreDefn.isAutoScore;
      });

    if (!hasManualSelected) {
      return null;
    }

    const tabItems_: ITabItem[] = [
      {
        content: <ScoresPanel />,
      },
    ];

    return (
      <BaseSidePanel
        headerTitle="Score"
        tabItems={tabItems_}
        customStyle={customStyles.root}
      />
    );
  }

  function getMainHeight() {
    return props.BottomMenu ? '95%' : '100%';
  }

  function designTabRenderer() {
    return (
      <div id="render-designtab-wrapper">
        <div
          id="render-designtab-clickarea"
          onClick={() => changeTabMode(DisplayModes.DESIGN)}
        >
          <CustomTab
            Icon={
              <RMSvgIcon
                id="render-designtab-design-icon"
                name="Design"
                {...customTabIconProps}
              />
            }
            id={DisplayModes.DESIGN}
            titleTip={'Design'}
            active={GetDisplayMode()}
          />
        </div>
      </div>
    );
  }

  function imageTabRenderer() {
    if (productEdition === EProductEditions.ExperimentDesigner) {
      return null;
    }

    return (
      <div id="render-imagetab-wrapper">
        <div
          id="render-imagetab-clickarea"
          onClick={() => changeTabMode(DisplayModes.IMAGE)}
        >
          <CustomTab
            Icon={
              <RMSvgIcon
                id="render-imagetab-images-icon"
                name="Images"
                {...customTabIconProps}
              />
            }
            id={DisplayModes.IMAGE}
            active={GetDisplayMode()}
            titleTip="Image"
          />
        </div>
      </div>
    );
  }

  const ingredientTabRenderer = () => {
    return (
      <div
        id="render-ingredienttab-wrapper"
        onClick={() => changeTabMode(DisplayModes.WELLINGREDIENT)}
      >
        <CustomTab
          Icon={
            <RMSvgIcon
              id="render-ingredienttab-images-icon"
              name="IngredientInfo"
              {...customTabIconProps}
            />
          }
          id={DisplayModes.WELLINGREDIENT}
          active={GetDisplayMode()}
          titleTip="Well Ingredients"
        />
      </div>
    );
  };

  function scoreViewRenderer() {
    return (
      <div
        id="render-scoreview-wrapper"
        onClick={() => changeTabMode(DisplayModes.SCORE)}
      >
        <CustomTab
          Icon={
            <RMSvgIcon
              id="render-scoreview-scoresbottommenu-icon"
              name="ScoresBottomMenu"
              {...customTabIconProps}
            />
          }
          id={DisplayModes.SCORE}
          active={GetDisplayMode()}
          titleTip="Scores"
        />
      </div>
    );
  }

  return (
    <div id="render-wrapper" className="expRouteMain">
      <div
        id="render-tabparent"
        className={`expRouteTabParent w-[${ExperimentSideBarWidth}px]`}
      >
        {/* DO NOT CHANGE THE ORDER, IF CHANGED THEN ALSO CHANGE IN getExperimentCanvasRoutes FUNCTION */}
        {renderDesignTab && designTabRenderer()}
        {(renderImageTab || rmLiteNewExp) && imageTabRenderer()}
        {ingredientTabRenderer()}
        {renderScoreView && scoreViewRenderer()}
      </div>
      <div
        id="render-content-wrapper"
        className="flex flex-column content-width bg-light"
      >
        <div
          id="render-mainheight-wrapper"
          style={{
            height: getMainHeight(),
            maxHeight: getMainHeight(),
            width: '100%',
          }}
        >
          <div
            id="render-experiment-wrapper"
            style={{ display: 'flex', height: '100%', width: '100%' }}
          >
            <Experiment
              expMode={expMode}
              scoreCommandPanel={scoreCommandPanelComponent()}
              selectedTabName={GetDisplayMode()}
              changeTabMode={changeTabMode}
              setIsExperimentLoaded={props.setIsExperimentLoaded}
              setSelectedSidePanelTab={props.setSelectedSidePanelTab}
              canvasWidth={canvasWidth - ExperimentSideBarWidth}
              {...rest}
            />
          </div>
        </div>
        {props.BottomMenu}
      </div>
    </div>
  );
});

const customStyles: IStyle = {
  root: {
    width: '100%',
  },
};
